// ---------- l'écran « Moi » : le joueur et ses badges ----------
// Le premier onglet du trophée (src/progression.ts). Il ne connaît AUCUNE règle : seuils,
// familles d'alphabets, catégories retenues, tout vient de `GET /me/badges`
// (backend/src/utils/badges.ts). Ce module ne fait que peindre ce qu'on lui dit — c'est ce qui
// permet de régler un seuil par un déploiement du Worker, sans attendre que tout le monde ait mis
// l'APK à jour.
//
// La dernière réponse est gardée sur l'appareil et peinte tout de suite à l'ouverture : l'écran
// s'affiche plein même hors ligne, et la réponse fraîche le remplace quand elle arrive.

import { escapeHtml } from './html';
import { currentUser, currentUsername, onUsernameChange, refreshUsername } from './account';
import {
  fetchBadges,
  puzzleCoverUrl,
  type BadgeState,
  type BadgeTier,
  type BadgesReport,
  type MeStats,
} from './api';
import { CATEGORY_LABELS, categoryMarkId } from './category';
import { progressIndex } from './progress';
import { openSheet } from './sheetLinks';
import { TIERS, fingerprint } from './badgeQueue';
import { alphabetLabel, currentLang, t, WEEK_START, weekdayInitials, type I18nKey } from './i18n';
import { openUsernameDialog } from './prefs';
import { installedAt } from './review';
import { PIGMENTS } from '../shared/alphabets/pointille';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

const CACHE_KEY = 'codigrid:badges:v1';
const DAY = 24 * 60 * 60 * 1000;
/** Le format du pseudo que pose le serveur (`guestN`) — même motif que RESERVED_GUEST_NAME dans
 *  prefs.ts. Un joueur qui le porte encore se voit proposer d'en choisir un. */
const GUEST = /^guest[0-9]+$/i;

// ---------- les glyphes des médailles ----------
// En trait blanc sur le métal, 24×24. Dessinés ici plutôt que dans le sprite de index.html : ils
// n'existent que sur ces médailles, et le sprite porte les icônes de l'interface. Les familles
// portent le VRAI geste de leur alphabet (des points, des formes, des hachures, un dé, des
// caractères) — c'est ce qui rend la médaille lisible sans son nom.
const GLYPHS: Record<string, string> = {
  library:
    '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="9.5" y="3" width="5" height="5" rx="1" opacity=".5"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="9.5" width="5" height="5" rx="1" opacity=".5"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/><rect x="16" y="9.5" width="5" height="5" rx="1" opacity=".5"/><rect x="3" y="16" width="5" height="5" rx="1"/><rect x="9.5" y="16" width="5" height="5" rx="1" opacity=".5"/><rect x="16" y="16" width="5" height="5" rx="1"/></svg>',
  daily:
    '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="5" width="18" height="16" rx="3" opacity=".4"/><rect x="3" y="5" width="18" height="5" rx="2"/><rect x="7" y="2.5" width="2" height="5" rx="1"/><rect x="15" y="2.5" width="2" height="5" rx="1"/><path d="M8.2 15.2l2.4 2.4 5.2-5.2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  share:
    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="18" cy="5.5" r="3.2"/><circle cx="6" cy="12" r="3.2"/><circle cx="18" cy="18.5" r="3.2"/><path d="M8.6 10.6l6.8-3.6M8.6 13.4l6.8 3.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  'family:dots':
    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="7" r="3.2"/><circle cx="16.5" cy="6" r="2"/><circle cx="12" cy="16" r="4"/><circle cx="20" cy="15" r="1.3"/><circle cx="4.5" cy="18" r="1.4"/></svg>',
  'family:shapes':
    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="7" cy="7" r="3.6"/><path d="M17 3.4l4 7h-8z"/><rect x="3.6" y="13.6" width="7" height="7" rx="1"/><path d="M17 13.2l1.4 2.9 3.1.4-2.3 2.2.6 3.1-2.8-1.5-2.8 1.5.6-3.1-2.3-2.2 3.1-.4z"/></svg>',
  'family:hatching':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M3 9L9 3M3 15L15 3M3 21L21 3M9 21L21 9M15 21l6-6"/></svg>',
  'family:dice':
    '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="18" height="18" rx="4" opacity=".35"/><circle cx="8" cy="8" r="1.8"/><circle cx="16" cy="8" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="8" cy="16" r="1.8"/><circle cx="16" cy="16" r="1.8"/></svg>',
  'family:ascii': '<span class="me-glyph-txt">#@%</span>',
  // Quatre briques vues de dessus, chacune avec son tenon (évidé) — la mosaïque de l'alphabet.
  'family:bricks':
    '<svg viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M3 3h8.5v8.5H3zm4.25 1.6a2.65 2.65 0 1 0 0 5.3 2.65 2.65 0 0 0 0-5.3zM12.5 3H21v8.5h-8.5zm4.25 1.6a2.65 2.65 0 1 0 0 5.3 2.65 2.65 0 0 0 0-5.3zM3 12.5h8.5V21H3zm4.25 1.6a2.65 2.65 0 1 0 0 5.3 2.65 2.65 0 0 0 0-5.3zM12.5 12.5H21V21h-8.5zm4.25 1.6a2.65 2.65 0 1 0 0 5.3 2.65 2.65 0 0 0 0-5.3z"/></svg>',
  // Une touche de pinceau en biais, et le pinceau qui la pose.
  'family:paint':
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.2 2.8a1.1 1.1 0 0 0-1.5 0l-8.2 8 2.1 2.1 8-8.2a1.1 1.1 0 0 0-.4-1.9z"/><path d="M10.4 12.1c-1.6-.3-3.4.6-3.9 2.4-.4 1.4-1.1 2.8-3.4 3.4 2.1 2.2 6 2.6 8.3.3 1.4-1.4 1.4-3 .9-4.2z"/></svg>',
  // Quatre croix de fil, deux pleines et deux à demi-teinte, comme une toile en cours.
  'family:embroidery':
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M4 4l6 6M10 4l-6 6M14 14l6 6M20 14l-6 6"/><path d="M14 4l6 6M20 4l-6 6M4 14l6 6M10 14l-6 6" opacity=".5"/></svg>',
  'category:celebrite':
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.6l2.7 5.6 6.1.8-4.5 4.2 1.1 6.1L12 16.4l-5.4 2.9 1.1-6.1L3.2 9l6.1-.8z"/></svg>',
  'category:paysage':
    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="17.5" cy="6.5" r="2.6"/><path d="M2 20l6.5-10 4 6 2.5-3.5L22 20z"/></svg>',
  'category:animaux':
    '<svg viewBox="0 0 24 24" fill="currentColor"><ellipse cx="6" cy="10" rx="2" ry="2.6"/><ellipse cx="10" cy="5.6" rx="2" ry="2.6"/><ellipse cx="14.6" cy="5.6" rx="2" ry="2.6"/><ellipse cx="18.4" cy="10" rx="2" ry="2.6"/><path d="M12.3 11.2c-2.6 0-5.3 3.6-5.3 6 0 1.7 1.3 2.6 2.7 2.6 1.1 0 1.7-.6 2.6-.6s1.5.6 2.6.6c1.4 0 2.7-.9 2.7-2.6 0-2.4-2.7-6-5.3-6z"/></svg>',
  'category:sport':
    '<svg viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 2.5a9.5 9.5 0 1 0 0 19 9.5 9.5 0 0 0 0-19zm0 4.3l3.4 2.5-1.3 4H9.9l-1.3-4zM5.4 9.7l1.6.5 1.4 4.2-1 1.4-2.6-.1a7.4 7.4 0 0 1 .6-6zm13.2 0a7.4 7.4 0 0 1 .6 6l-2.6.1-1-1.4 1.4-4.2zM9.6 17l.9-1.3h3l.9 1.3-.8 2.3a7.5 7.5 0 0 1-3.2 0z"/></svg>',
  'category:art':
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8C6.6 2.8 2.6 6.7 2.6 11.6c0 4.5 3.6 8.5 8.2 8.5 1.4 0 2-.8 2-1.7 0-1.3-1-1.6-1-2.8 0-1 .8-1.7 1.9-1.7h2.6c2.6 0 5.1-1.7 5.1-4.6 0-3.9-4.2-6.5-9.4-6.5zM7 12.6a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zm2.8-4a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zm4.6 0a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zm3 3.4a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2z"/></svg>',
};
// Les autres catégories (2026-10-14) : l'icône de leur catégorie, prise dans le sprite d'index.html
// plutôt que redessinée ici. Le joueur reconnaît alors sur la médaille le dessin qu'il voit sur
// les cartes du catalogue et sur les packs — une catégorie, un dessin, partout.
for (const slug of [
  'monuments',
  'scenes',
  'personnes',
  'contes',
  'saisonnier',
  'vehicules',
  'divers',
])
  GLYPHS[`category:${slug}`] =
    `<svg viewBox="0 0 256 256" fill="currentColor"><use href="#${categoryMarkId(slug)}"></use></svg>`;

/** Un badge que le serveur ajouterait avant que l'APK ne le connaisse : une étoile plutôt qu'une
 *  médaille vide. */
const FALLBACK_GLYPH = GLYPHS['category:celebrite'];

/** Le glyphe d'un badge, seul — l'onglet « Ensemble » (src/together.ts) en pose un sur
 *  « l'alphabet du moment », avec le même dessin que la médaille de la famille. */
export function badgeGlyph(id: string): string {
  return GLYPHS[id] ?? FALLBACK_GLYPH;
}

// ---------- la rareté : « 6 % des joueurs » ----------
// Ce qui reste de la ligue, sans classement : on compare des BADGES, jamais des personnes, et
// personne n'est dernier. Calculée par le serveur sur tous les joueurs, une fois par heure (cf.
// tallyRarity dans backend/src/utils/community.ts).

/** Sous vingt joueurs, un pourcentage ment par sa précision : un seul joueur de plus fait bouger
 *  le chiffre de cinq points, et « 50 % des joueurs » sur deux joueurs ne dit rien de vrai. La
 *  ligne ne s'affiche donc qu'au-delà. */
const MIN_RARITY_PLAYERS = 20;
/** « Rare », et la couleur d'or, sous un joueur sur dix. Au-dessus, le pourcentage seul. */
const RARE = 0.1;
const PEOPLE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="9" cy="8" r="3.4"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M16 4.6a3.4 3.4 0 0 1 0 6.8M18 14.4c1.8.8 3 2.6 3 5.6"/></svg>';

function rarityLine(b: BadgeState): string {
  const rarity = current?.rarity;
  if (!rarity || rarity.players < MIN_RARITY_PLAYERS) return '';
  const r = rank(b);
  // Un badge pas encore obtenu dit combien ont son PREMIER palier : « déjà obtenu par 31 % » est
  // une invitation, là où le palier d'un autre ne dirait rien à qui n'a pas encore commencé.
  const n = rarity.held[b.id]?.[Math.max(r, 0)] ?? 0;
  // Personne d'autre — ou l'instantané, vieux d'une heure au plus, ne connaît pas encore ce
  // palier tout juste gagné : on ne dit rien plutôt que « 0 % ».
  if (!n) return '';
  const share = n / rarity.players;
  const pct = share < 0.01 ? '&lt;1' : String(Math.round(share * 100));
  if (r < 0) return `<span class="me-rarity locked">${t('me.rarity.locked', { pct })}</span>`;
  const rare = share < RARE;
  return `<span class="me-rarity${rare ? ' rare' : ''}">${PEOPLE}${t(
    rare ? 'me.rarity.rare' : 'me.rarity',
    { pct },
  )}</span>`;
}
const LOCK =
  '<span class="me-lock"><svg viewBox="0 0 256 256" fill="currentColor"><path d="M208,80H176V56a48,48,0,0,0-96,0V80H48A16,16,0,0,0,32,96V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V96A16,16,0,0,0,208,80ZM96,56a32,32,0,0,1,64,0V80H96Z"/></svg></span>';
const PENCIL =
  '<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z"/></svg>';

// ---------- lecture ----------

/** Le cache porte l'empreinte du compte qui l'a reçu (`owner`), et n'est relu que pour lui. Sans
 *  elle, un code de reprise échangé ou un compte recréé montrait un moment les badges de l'AUTRE
 *  compte — et la célébration en tirait son relevé « déjà vu » (cf. src/celebrate.ts). Un cache
 *  sans empreinte (écrit avant ce champ) est ignoré : il se refait à la première réponse. */
function readCache(owner: string): BadgesReport | null {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as
      (BadgesReport & { owner?: string }) | null;
    return raw && Array.isArray(raw.badges) && raw.owner === owner ? raw : null;
  } catch {
    return null;
  }
}

function writeCache(r: BadgesReport, owner: string): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...r, owner }));
  } catch {
    /* sans cache, l'écran attend simplement le réseau à la prochaine ouverture */
  }
}

/** Échappe un texte libre (le pseudo) avant de le poser dans du HTML. */
const esc = escapeHtml;

function rank(b: BadgeState): number {
  return b.tier ? TIERS.indexOf(b.tier) : -1;
}

/** Repli sur l'identifiant : un badge que le serveur ajouterait avant que l'APK ne connaisse son
 *  nom s'affiche « category:musique » plutôt que vide (cf. FALLBACK_GLYPH pour sa médaille). */
export function badgeName(b: BadgeState): string {
  const name =
    b.group === 'category'
      ? t(`home.category.${b.id.slice(9)}` as I18nKey)
      : t(`badge.${b.id.replace(':', '.')}` as I18nKey);
  return name || b.id;
}

function unit(b: BadgeState, n: number): string {
  const kind = b.id === 'share' ? 'shares' : 'puzzles';
  return t(`badge.unit.${kind}.${n === 1 ? 'one' : 'other'}` as I18nKey, { n });
}

function goal(b: BadgeState): string {
  if (b.group === 'family') {
    return t('badge.goal.family', {
      alphabets: (b.alphabets ?? []).map((a) => alphabetLabel(a).toLowerCase()).join(', '),
    });
  }
  if (b.group === 'category') return t('badge.goal.category', { category: badgeName(b) });
  return t(`badge.goal.${b.id}` as I18nKey);
}

// ---------- les pièces de l'écran ----------

export function medal(b: BadgeState, size = ''): string {
  const r = rank(b);
  const cls = r < 0 ? 'none' : TIERS[r];
  // Le reflet ne passe que sur l'Or et au-dessus : sur toutes les médailles à la fois, l'écran
  // scintillerait de partout et plus rien ne se distinguerait.
  return `<span class="me-medal ${cls} ${size}">${GLYPHS[b.id] ?? FALLBACK_GLYPH}${
    r >= 2 ? '<span class="me-shine"></span>' : ''
  }${r < 0 ? LOCK : ''}</span>`;
}

function pips(b: BadgeState): string {
  const r = rank(b);
  return `<span class="me-pips" aria-hidden="true">${b.thresholds
    .map((_, i) => `<i class="${i <= r ? TIERS[i] : ''}${i === 4 ? ' p5' : ''}"></i>`)
    .join('')}</span>`;
}

/** La barre va du palier retenu au suivant. Le compteur réel peut être SOUS le palier retenu
 *  (une feuille recommencée) : la barre est alors vide, pas négative. */
export function progress(b: BadgeState): { pct: number; line: string } {
  const r = rank(b);
  const last = b.thresholds.length - 1;
  if (r >= last) {
    return {
      pct: 100,
      line: `${t('badge.reached', { tier: t(`progression.tier.${TIERS[r]}` as I18nKey) })} · ${unit(b, b.count)}`,
    };
  }
  const lo = r < 0 ? 0 : b.thresholds[r];
  const hi = b.thresholds[r + 1];
  const pct = Math.max(0, Math.min(100, ((b.count - lo) / (hi - lo)) * 100));
  return {
    pct: Math.max(pct, b.count > lo ? 4 : 0),
    line: `<b>${b.count} / ${hi}</b> ${t(`badge.next.${TIERS[r + 1]}` as I18nKey)}`,
  };
}

function card(b: BadgeState): string {
  const r = rank(b);
  const { pct, line } = progress(b);
  // « À débloquer » sur les jalons seulement : sur une famille ou une catégorie, la carte grisée
  // et son cadenas le disent déjà, et le mot répété neuf fois chargeait la liste pour rien.
  const tierLabel =
    r >= 0
      ? `<span class="me-tier t-${TIERS[r]}">${t(`progression.tier.${TIERS[r]}` as I18nKey)}</span>`
      : b.group === 'milestone'
        ? `<span class="me-tier t-none">${t('badge.locked')}</span>`
        : '';
  return `
    <button class="me-badge${r < 0 ? ' locked' : ''}" data-badge="${b.id}">
      ${medal(b, 'lg')}
      <span class="me-badge-body">
        <span class="me-badge-top"><span class="me-badge-name">${badgeName(b)}</span>${tierLabel}</span>
        ${rarityLine(b)}
        ${pips(b)}
        <span class="me-bar"><i style="width:${pct}%"></i></span>
        <span class="me-next">${line}</span>
      </span>
    </button>`;
}

function section(title: string, list: BadgeState[]): string {
  if (!list.length) return '';
  let got = 0;
  let max = 0;
  for (const b of list) {
    got += rank(b) + 1;
    max += b.thresholds.length;
  }
  return `
    <div class="me-sec-h"><h2>${title}</h2><span>${got} / ${max}</span></div>
    <div class="me-list">${list.map(card).join('')}</div>`;
}

function sinceLine(report: BadgesReport | null): string {
  // L'arrivée du COMPTE plutôt que l'installation de cet appareil : elle suit le joueur qui change
  // de téléphone avec son code de reprise. La date locale ne sert qu'avant la première réponse.
  const since = report?.since ? Date.parse(report.since) : installedAt();
  const date = new Intl.DateTimeFormat(currentLang(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(since);
  const days = Math.max(1, Math.floor((Date.now() - since) / DAY) + 1);
  return `${t('me.since', { date: `<b>${date}</b>` })} · ${t(`me.days.${days === 1 ? 'one' : 'other'}`, { n: days })}`;
}

function identity(report: BadgesReport | null, name: string | null): string {
  const guest = !name || GUEST.test(name);
  const by = (id: string) => report?.badges.find((b) => b.id === id)?.count ?? 0;
  const families = report?.badges.filter((b) => b.group === 'family') ?? [];
  const played = families.filter((b) => b.count > 0).length;
  return `
    <section class="me-card">
      <canvas class="me-dots" id="meDots" width="300" height="240" aria-hidden="true"></canvas>
      <div class="me-id">
        <div class="me-avatar"><canvas id="meAvatar" width="128" height="128" aria-hidden="true"></canvas></div>
        <div class="me-who">
          <div class="me-eyebrow">${t('me.eyebrow')}</div>
          <div class="me-name${guest ? ' guest' : ''}">
            <strong>${esc(name ?? '—')}</strong>
            <button class="me-edit" id="meEdit" aria-label="${esc(t('me.edit'))}">${PENCIL}</button>
          </div>
          <div class="me-since">${sinceLine(report)}</div>
        </div>
      </div>
      ${guest ? `<button class="me-choose" id="meChoose"><span>${t('me.choose')}</span><svg class="i" aria-hidden="true"><use href="#i-go"></use></svg></button>` : ''}
      <div class="me-stats">
        <div class="me-stat"><div class="v">${by('library') + by('daily')}</div><div class="l">${t('me.stat.finished')}</div></div>
        <div class="me-stat"><div class="v">${by('daily')}</div><div class="l">${t('me.stat.daily')}</div></div>
        <div class="me-stat"><div class="v">${played} / ${families.length || 5}</div><div class="l">${t('me.stat.styles')}</div></div>
      </div>
    </section>`;
}

function tally(badges: BadgeState[]): string {
  let got = 0;
  let max = 0;
  const per: Record<BadgeTier, number> = { bronze: 0, argent: 0, or: 0, platine: 0, prisme: 0 };
  for (const b of badges) {
    got += rank(b) + 1;
    max += b.thresholds.length;
    if (b.tier) per[b.tier]++;
  }
  return `
    <div class="me-tally">
      <div class="me-tally-n">${got}<small>/${max}</small></div>
      <div class="me-tally-t">${t('me.tally')}</div>
      <div class="me-metals">${TIERS.map(
        (k) =>
          `<span class="me-metal" title="${t(`progression.tier.${k}` as I18nKey)}"><i class="m-${k}"></i>${per[k]}</span>`,
      ).join('')}</div>
    </div>`;
}

// ---------- tes chiffres ----------
// Quatre choses que le joueur ne voyait nulle part (2026-10-14) : toutes ses cases, son style, son
// plus grand puzzle, et quand il a joué. Toutes viennent de la réponse des badges, déjà chargée
// par cet onglet (`stats`, cf. GET /me/badges) : aucune requête de plus. Absentes d'un cache
// d'avant ce champ ou d'un serveur plus ancien, la section se tait simplement.

const fmt = (n: number): string => new Intl.NumberFormat(currentLang()).format(n);

/** Le nombre de semaines du calendrier d'activité : ce que le serveur renvoie (92 jours) moins
 *  la marge du fuseau. */
const WEEKS = 12;

/** La clé d'un jour LOCAL : un puzzle fini à 23 h 30 à Paris est de ce jour-là, pas du lendemain
 *  UTC — c'est pour ça que le serveur envoie des instants et non des jours. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Le calendrier d'activité : douze semaines, une colonne par semaine, une case par jour, plus
 * foncée quand on a fini plus de puzzles ce jour-là. Les semaines commencent le jour que la langue
 * attend (WEEK_START), comme le calendrier du puzzle du jour.
 *
 * TOUT CE QU'IL FAUT POUR LE LIRE EST SUR LA CARTE (2026-10-14). La première version n'était qu'une
 * grille de carrés sous « Ton activité », et elle n'a pas été comprise : rien ne disait qu'une
 * case est un jour, ni dans quel sens va le temps, ni ce que veut dire une case plus foncée. D'où
 * le titre qui dit la période, la phrase qui dit la règle, les initiales des jours, « il y a 12
 * semaines » → « aujourd'hui » sous la grille, la case du jour entourée, et la légende.
 */
function activity(s: MeStats): string {
  const per = new Map<string, number>();
  for (const at of s.finishedAt) {
    const d = new Date(at);
    if (!Number.isNaN(d.getTime())) per.set(dayKey(d), (per.get(dayKey(d)) ?? 0) + 1);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Le premier jour de la semaine en cours, dans la convention de la langue (lundi ou dimanche).
  const first = WEEK_START[currentLang()] === 7 ? 0 : 1;
  const back = (today.getDay() - first + 7) % 7;
  const start = new Date(today);
  start.setDate(today.getDate() - back - (WEEKS - 1) * 7);
  let active = 0;
  const cells: string[] = [];
  for (let w = 0; w < WEEKS; w++) {
    for (let d = 0; d < 7; d++) {
      const day = new Date(start);
      day.setDate(start.getDate() + w * 7 + d);
      if (day > today) {
        cells.push('<i class="future"></i>');
        continue;
      }
      const n = per.get(dayKey(day)) ?? 0;
      if (n) active++;
      const now = day.getTime() === today.getTime() ? ' today' : '';
      cells.push(`<i class="l${Math.min(3, n)}${now}"></i>`);
    }
  }
  const legend = [0, 1, 2, 3].map((l) => `<i class="l${l}"></i>`).join('');
  return `
    <div class="tg-card me-activity">
      <span class="tg-eyebrow">${t('me.activity.title')}</span>
      <span class="tg-count">${t('me.activity.how')}</span>
      <div class="me-cal" aria-hidden="true">
        <div class="me-cal-days">${weekdayInitials()
          .map((d) => `<span>${esc(d)}</span>`)
          .join('')}</div>
        <div class="me-heat">${cells.join('')}</div>
      </div>
      <div class="me-cal-axis" aria-hidden="true">
        <span>${t('me.activity.ago')}</span><span>${t('me.activity.today')}</span>
      </div>
      <div class="me-cal-foot">
        <span class="tg-count">${
          // Vide, la grille dit ce qui la remplira plutôt que « 0 jour » : un calendrier gris sans
          // explication ressemble à un chargement raté.
          active
            ? t(`me.activity.${active === 1 ? 'one' : 'other'}`, { n: fmt(active) })
            : t('me.activity.none')
        }</span>
        <span class="me-legend" aria-hidden="true">${t('me.activity.less')}${legend}${t('me.activity.more')}</span>
      </div>
    </div>`;
}

/** Le plus grand puzzle fini : sa vignette (celle de l'appareil, sinon la couverture du serveur
 *  — le joueur l'a fini, il n'y a rien à cacher), son titre, ses cases. Une carte qui se touche :
 *  elle rouvre la feuille. */
function biggest(s: MeStats): string {
  const b = s.biggest;
  if (!b) return '';
  const src = progressIndex().get(b.puzzleId)?.thumb ?? puzzleCoverUrl(b.puzzleId);
  return `
    <button class="tg-card me-biggest" id="meBiggest">
      <img class="tg-thumb" src="${esc(src)}" alt="" loading="lazy" />
      <span class="me-biggest-t">
        <span class="tg-eyebrow">${t('me.biggest.eyebrow')}</span>
        <span class="tg-name">${esc(b.title.trim() || t('me.biggest.untitled'))}</span>
        <span class="tg-count">${t('me.biggest.cells', { n: fmt(b.cells) })}</span>
      </span>
      <svg class="i" aria-hidden="true"><use href="#i-go"></use></svg>
    </button>`;
}

function numbers(report: BadgesReport | null): string {
  const s = report?.stats;
  if (!s) return '';
  const fam = s.favorites.family;
  const cat = s.favorites.category;
  // AVANT LE PREMIER PUZZLE FINI, la carte reste et dit comment se découvrir : le style se tire
  // des puzzles FINIS, et une carte « Cases peintes » seule à côté d'un trou se lisait comme un
  // écran cassé (remonté le 2026-10-14, sur un compte à trois puzzles commencés).
  const style = `
      <div class="tg-card tg-stat">
        <span class="tg-eyebrow">${t('me.fav.eyebrow')}</span>
        ${
          fam
            ? `<span class="tg-name me-fav">${t(`badge.family.${fam}` as I18nKey) || esc(fam)}</span>
               ${cat ? `<span class="tg-count">${t('me.fav.cat', { cat: esc(CATEGORY_LABELS[cat] ?? cat) })}</span>` : ''}`
            : `<span class="tg-name me-fav">${t('me.fav.none')}</span>
               <span class="tg-count">${t('me.fav.noneHint')}</span>`
        }
      </div>`;
  return `
    <div class="me-sec-h"><h2>${t('me.numbers')}</h2></div>
    <div class="tg-pair">
      <div class="tg-card tg-stat">
        <span class="tg-eyebrow">${t('me.cells.eyebrow')}</span>
        <span class="tg-stat-n">${fmt(s.cells)}</span>
        <span class="tg-count">${t('me.cells.what')}</span>
      </div>
      ${style}
    </div>
    ${biggest(s)}
    ${activity(s)}`;
}

// ---------- les deux dessins ----------

/** Le motif de points dans le coin de la carte d'identité : un rappel du pointillisme. */
function drawDots(): void {
  const c = document.getElementById('meDots') as HTMLCanvasElement | null;
  const x = c?.getContext('2d');
  if (!c || !x) return;
  x.fillStyle = '#ffffff';
  for (let r = 0; r < 12; r++) {
    for (let q = 0; q < 15; q++) {
      const rad = 7 * (1 - Math.hypot(q - 15, r) / 15);
      if (rad < 0.6) continue;
      x.beginPath();
      x.arc(q * 20 + 10, r * 20 + 10, rad, 0, Math.PI * 2);
      x.fill();
    }
  }
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * L'avatar : une mosaïque symétrique de 6×6 points, tirée du pseudo, peinte avec les neuf
 * pigments du pointillisme. Pas de photo à demander, pas d'image à stocker, et chaque nom a la
 * sienne — le joueur est représenté par ce qu'il fait dans le jeu.
 */
function drawAvatar(name: string): void {
  const c = document.getElementById('meAvatar') as HTMLCanvasElement | null;
  if (c) paintAvatar(c, name);
}

/** La mosaïque elle-même, sur n'importe quel canevas de 128 px — l'écran « Moi », et la carte du
 *  créateur dans Réglages (src/maker.ts), qui y peint « tidou44 » : l'auteur est représenté
 *  comme n'importe quel joueur. */
export function paintAvatar(c: HTMLCanvasElement, name: string): void {
  const x = c.getContext('2d');
  if (!x) return;
  const n = 6;
  const z = 128 / n;
  let h = hash(name);
  x.fillStyle = '#fbf6ec'; // le papier, qui ne change pas de thème
  x.fillRect(0, 0, 128, 128);
  for (let r = 0; r < n; r++) {
    for (let q = 0; q < n / 2; q++) {
      h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0;
      if (h % 5 === 0) continue;
      x.fillStyle = PIGMENTS[h % PIGMENTS.length].css;
      const rad = z * (0.28 + ((h >>> 8) % 3) * 0.08);
      for (const col of [q, n - 1 - q]) {
        x.beginPath();
        x.arc(col * z + z / 2, r * z + z / 2, rad, 0, Math.PI * 2);
        x.fill();
      }
    }
  }
}

// ---------- l'écran ----------

let current: BadgesReport | null = null;
/** L'empreinte du compte à qui appartient `current`. */
let currentOwner: string | null = null;
/** Quand `current` est arrivé du serveur. Ouvrir « Moi », passer à « Ensemble », revenir :
 *  chaque passage redemandait les badges. Une réponse de moins de cinq minutes resert telle
 *  quelle — une victoire, elle, redemande d'elle-même (src/celebrate.ts). */
let freshAt = 0;
const FRESH_MS = 5 * 60 * 1000;
let shownName: string | null = null;
let visible = false;
/** Qui veut savoir qu'une réponse fraîche vient d'arriver — la célébration (src/celebrate.ts),
 *  qui y cherche des paliers nouveaux. Un rappel posé de l'extérieur plutôt qu'un import : ce
 *  module-ci peint, il n'a pas à connaître ce qui se passe après. */
let freshListener: ((r: BadgesReport, owner: string) => void) | null = null;

export function onFreshBadges(fn: (r: BadgesReport, owner: string) => void): void {
  freshListener = fn;
}

/** La dernière réponse connue POUR CE COMPTE, du cache s'il le faut — sans réseau. Ce qui
 *  appartient à un autre compte est oublié. */
export function cachedBadges(owner: string): BadgesReport | null {
  if (currentOwner !== owner) {
    current = readCache(owner);
    currentOwner = owner;
    freshAt = 0;
  }
  return current;
}

/** Une réponse obtenue ailleurs que par l'écran (la célébration la demande après une victoire) :
 *  elle devient la vérité de l'écran aussi, et le repeint s'il est là. */
export function rememberBadges(r: BadgesReport, owner: string): void {
  current = r;
  currentOwner = owner;
  freshAt = Date.now();
  writeCache(r, owner);
  if (visible) void paint($('progressionBody'));
}

/** L'écran « Moi » est à l'écran — la seule place où une célébration peut s'ouvrir d'elle-même. */
export function isMeVisible(): boolean {
  return visible;
}

async function paint(host: HTMLElement): Promise<void> {
  shownName = await currentUsername();
  if (!visible) return;
  if (!current) {
    host.innerHTML = `<p class="prog-empty">${t('progression.loading')}</p>`;
    return;
  }
  const groups = (g: BadgeState['group']) => current!.badges.filter((b) => b.group === g);
  // Les catégories dans l'ordre du filtre du catalogue, et pas dans celui du serveur, qui garde
  // les quatre premières en tête pour les versions qui n'affichent qu'elles (cf. BADGES).
  const order = Object.keys(CATEGORY_LABELS);
  const rankOf = (b: BadgeState) => {
    const i = order.indexOf(b.id.slice('category:'.length));
    return i < 0 ? order.length : i;
  };
  const categories = groups('category').sort((a, b) => rankOf(a) - rankOf(b));
  host.innerHTML = `
    <div class="me">
      ${identity(current, shownName)}
      ${tally(current.badges)}
      ${numbers(current)}
      ${section(t('me.section.milestones'), groups('milestone'))}
      ${section(t('me.section.families'), groups('family'))}
      ${section(t('me.section.categories'), categories)}
    </div>`;
  drawDots();
  drawAvatar(shownName ?? '');
  const edit = (): void => void openUsernameDialog();
  $('meEdit').onclick = edit;
  const choose = document.getElementById('meChoose');
  if (choose) choose.onclick = edit;
  const big = document.getElementById('meBiggest');
  const bigId = current.stats?.biggest?.puzzleId;
  if (big && bigId) big.onclick = () => openSheet(bigId);
  // Une couverture introuvable (feuille sans couverture) : la carte garde son texte, sans cadre vide.
  const img = big?.querySelector('img');
  if (img) img.onerror = () => img.remove();
  for (const el of host.querySelectorAll<HTMLElement>('.me-badge')) {
    el.onclick = () => {
      const b = current?.badges.find((x) => x.id === el.dataset['badge']);
      if (b) openBadgeSheet(b);
    };
  }
}

/** Peint l'onglet tout de suite (cache), puis le rafraîchit depuis le serveur. */
export async function showMe(host: HTMLElement): Promise<void> {
  visible = true;
  // Le jeton d'abord : le cache ne se relit que pour le compte qui l'a reçu.
  const token = await currentUser();
  if (!token) {
    // Pas encore de compte : il naît à la première sauvegarde, donc ce joueur n'a encore rien fini.
    // `visible` revérifié : le joueur a pu changer d'onglet pendant l'attente du jeton.
    current = null;
    currentOwner = null;
    if (visible) host.innerHTML = `<p class="prog-empty">${t('me.empty')}</p>`;
    return;
  }
  const owner = fingerprint(token);
  cachedBadges(owner);
  // Un cache d'une forme inattendue (un serveur plus récent, un champ disparu) ne doit pas faire
  // échouer tout l'onglet en silence : il est oublié, et la réponse fraîche repeindra.
  try {
    await paint(host);
  } catch {
    current = null;
    freshAt = 0;
  }
  if (!shownName) void refreshUsername(); // l'écouteur repeindra le nom à son retour
  if (current && Date.now() - freshAt < FRESH_MS) return;
  try {
    const fresh = await fetchBadges(token);
    // Le compte a changé pendant la requête (code de reprise, jeton re-créé) : cette réponse est
    // celle de l'ANCIEN compte, elle ne doit ni s'afficher ni nourrir la célébration.
    if ((await currentUser()) !== token) return;
    rememberBadges(fresh, owner); // repeint l'écran s'il est toujours là
    freshListener?.(fresh, owner);
  } catch {
    if (visible && !current)
      host.innerHTML = `<p class="prog-empty">${t('progression.loadFailed')}</p>`;
  }
}

/** L'onglet n'est plus à l'écran (autre onglet, ou trophée refermé) : une réponse qui arrive
 *  après ne doit pas repeindre par-dessus la ligue. */
export function hideMe(): void {
  visible = false;
}

// ---------- le détail d'un badge ----------

function openBadgeSheet(b: BadgeState): void {
  const r = rank(b);
  $('badgeSheetTitle').textContent = badgeName(b);
  const rows = b.thresholds
    .map((goalN, i) => {
      const tier = TIERS[i];
      const done = i <= r;
      const next = i === r + 1;
      const status = done
        ? `<span class="me-st ok">${t('badge.earned')}</span>`
        : next
          ? `<span class="me-st">${b.count} / ${goalN}</span>`
          : '<span class="me-st">—</span>';
      return `
        <div class="me-tier-row${next ? ' cur' : ''}">
          <span class="me-medal sm ${done ? tier : 'none'}">${GLYPHS[b.id] ?? FALLBACK_GLYPH}</span>
          <span class="me-tier-n"><b>${t(`progression.tier.${tier}` as I18nKey)}</b><span>${unit(b, goalN)}</span></span>
          ${status}
        </div>`;
    })
    .join('');
  $('badgeSheetBody').innerHTML = `
    <div class="me-sheet-head">${medal(b, 'xl')}<p>${goal(b)}</p></div>
    <div class="me-tiers">${rows}</div>`;
  $('badgeSheet').setAttribute('aria-hidden', 'false');
}

/** Même contrat que closeLangSheet() : vrai seulement si le geste a refermé quelque chose — ce
 *  qu'attend la chaîne du bouton retour d'Android (main.ts). */
export function closeBadgeSheet(): boolean {
  if ($('badgeSheet').getAttribute('aria-hidden') !== 'false') return false;
  $('badgeSheet').setAttribute('aria-hidden', 'true');
  return true;
}

export function wireProfile(): void {
  $('badgeSheetClose').onclick = () => closeBadgeSheet();
  // Le fond referme, comme la feuille de la langue : on quitte un détail en tapant à côté.
  $('badgeSheet').onclick = (e) => {
    if (e.target === $('badgeSheet')) closeBadgeSheet();
  };
  // Le pseudo vient de changer (le dialogue ouvert par le crayon, ou un autre chemin) : on
  // repeint la carte d'identité si l'écran est là.
  onUsernameChange(() => {
    if (visible) void paint($('progressionBody'));
  });
}
